import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {base, useEvaluacionInvalidation, type BateriaForma, type BateriaPaquete} from '@/hooks/bateria/use-bateria'
import type {components} from '@/lib/api-schema'

export function useReasignarPaquete(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({paqueteId, employeeId}: {paqueteId: string; employeeId: string}) =>
      (await api.post<BateriaPaquete>(`${base(businessId)}/paquetes/${paqueteId}/reasignar`, {employee_id: employeeId}))
        .data,
    onSuccess: invalidate,
  })
}

export type CorregirIdentidad = components['schemas']['CorregirIdentidadCreate']

/** G25/v2 §2.6: los 409 de batería llegan con `motivo` estructurado; los lee el diálogo, no `translateApiError`. */
export function useCorregirIdentidad(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({paqueteId, payload}: {paqueteId: string; payload: CorregirIdentidad}) =>
      (await api.patch<BateriaPaquete>(`${base(businessId)}/paquetes/${paqueteId}/persona`, payload)).data,
    onSuccess: invalidate,
  })
}

/** G26/v2 §2.5: solo sin páginas leídas (si no, 409 `paginas_leidas`); regenera el `qr_uuid` y el cuadernillo
 * viejo queda muerto. */
export function useCambiarForma(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({paqueteId, forma}: {paqueteId: string; forma: BateriaForma}) =>
      (await api.post<BateriaPaquete>(`${base(businessId)}/paquetes/${paqueteId}/cambiar-forma`, {forma})).data,
    onSuccess: invalidate,
  })
}

export type PaginaHistorial = components['schemas']['BateriaPaginaHistorialRead']

export function usePaginaHistorial(
  businessId: string | undefined,
  paqueteId: string | undefined,
  numero: number | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['bateria-pagina-historial', businessId, paqueteId, numero],
    queryFn: async () =>
      (await api.get<PaginaHistorial>(`${base(businessId!)}/paquetes/${paqueteId}/paginas/${numero}/historial`)).data,
    enabled: enabled && !!businessId && !!paqueteId && numero !== undefined,
  })
}

/** La restauración queda en el historial (`origen: "restauracion"`): se deshace con otro restaurar. */
export function useRestaurarPagina(businessId: string, evaluacionId: string, paqueteId: string) {
  const queryClient = useQueryClient()
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({numero, version}: {numero: number; version: number}) =>
      (
        await api.post<BateriaPaquete>(`${base(businessId)}/paquetes/${paqueteId}/paginas/${numero}/restaurar`, {
          version,
        })
      ).data,
    onSuccess: (_data, {numero}) => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['bateria-pagina-historial', businessId, paqueteId, numero]})
    },
  })
}
